<script>
import BaseDraggableList from '@/components/_base-draggable-list.vue'
import { MaxPreferredQueueSize } from '@/shared/constants/booking/BookingSelectEducators'
import { insertAt } from '@/helpers/reorderable-array-helpers.js'
import Colours from '@/shared/constants/design/Colours.js'
import PreferredEducatorCard from './preferred-educator-card.vue'

export default {
  name: 'PreferredEducatorQueue',
  components: { BaseDraggableList, PreferredEducatorCard },
  props: {
    educators: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      Colours,
    }
  },
  computed: {
    maxQueueSize: () => MaxPreferredQueueSize,
    isQueueFull() {
      return this.educators.length >= MaxPreferredQueueSize
    },
  },
  methods: {
    async onExternalDrop({ item: educator, targetIndex }) {
      const isDuplicate = this.educators.some(({ id }) => id === educator?.id)
      if (isDuplicate) return

      this.$emit('change', insertAt(this.educators, educator, targetIndex))

      await this.$nextTick()

      if (this.isQueueFull) {
        this.$toast.warning(
          this.$t('bookingCreateV2.selectEducatorsStep.queueFullWarningToast')
        )
      }
    },
  },
}
</script>

<template>
  <v-card
    class="fill-height d-flex flex-column p-lg rounded-lg"
    data-testid="preferred-educator-queue"
  >
    <div class="mb-3">
      <h3 class="display-xs mb-1">
        {{ $t('bookingCreateV2.selectEducatorsStep.preferredOrderTitle') }}
      </h3>
      <p class="text-xs-regular neutrals-grey-500-text mb-0">
        {{
          $t('bookingCreateV2.selectEducatorsStep.preferredOrderDescription')
        }}
      </p>
    </div>

    <div
      class="preferred-queue pa-2 overflow-y-auto rounded-lg border-grey-200 border-dashed border-width-2"
    >
      <BaseDraggableList
        class="fill-height reorderable-list"
        :items="educators"
        group="preferred-educator"
        :accepts-groups="['bookable-educator']"
        drag-enabled
        reorderable
        :drop-indicator="['bookable-educator']"
        :reject-duplicate-drops="['bookable-educator']"
        disable-leave-animation
        fill-drop-area
        @drag-start="$emit('drag-state-change', true)"
        @drag-end="$emit('drag-state-change', false)"
        @change="$emit('change', $event)"
        @external-drop="onExternalDrop"
      >
        <template v-slot:empty>
          <div
            class="d-flex flex-column align-center justify-center pa-6 rounded-xl fill-height"
            data-testid="queue-empty-state"
          >
            <p class="text-md-bold mb-2">{{
              $t('bookingCreateV2.selectEducatorsStep.queueEmptyTitle')
            }}</p>
            <p class="text-md neutrals-grey-500-text text-center mb-0">
              {{
                $t('bookingCreateV2.selectEducatorsStep.queueEmptyHint', [
                  $t('app.companyNameShort'),
                ])
              }}
            </p>
          </div>
        </template>

        <template
          v-slot="{
            item: educator,
            index,
            moveUp,
            moveDown,
            remove,
            isFirst,
            isLast,
          }"
        >
          <PreferredEducatorCard
            :educator="educator"
            :index="index"
            :is-first="isFirst"
            :is-last="isLast"
            @move-up="moveUp"
            @move-down="moveDown"
            @remove="remove"
          />
        </template>
      </BaseDraggableList>
    </div>
    <v-alert
      :class="[isQueueFull ? 'opacity-100' : 'opacity-0']"
      :color="Colours.brandBlue25"
      text
      dense
      class="transition-opacity rounded-lg brand-blue-600-text mt-4 mb-0"
      data-testid="queue-full-alert"
    >
      {{
        $t('bookingCreateV2.selectEducatorsStep.queueFullMessage', [
          maxQueueSize,
        ])
      }}
    </v-alert>
  </v-card>
</template>
